// ============================================================
// SkillsCard - full-catalog clickable skill tests (34 skills)
// for both Overview sheets. Evolves the old SkillTestList:
// keeps data-testid="skill-test-list" and the untrained tag.
// Untrained ADVANCED skills are disabled (not rollable).
// The Parry row gets a weapon selector (v6.32.0): defaults to
// the defence chain's auto-best pick (findParryWeaponIndex),
// its modifier applied to the roll and named in the narrative.
// ============================================================
import { memo, useMemo, useState } from 'react';
import styles from '../../styles/ActionSheet.module.css';
import { SKILL_TEST_DEFS, calcSkillTarget, CHARACTERISTIC_TEST_LABELS } from '../../engine/characteristicTests';
import type { SkillTestDef } from '../../engine/characteristicTests';
import { calcWeaponParryMod } from '../../engine/parryWeapon';
import { hasWS } from '../../engine/weaponSpecials';
import type { Character } from '../../engine/character/types';

interface ParryWeaponOption {
  name: string;
  type: string;
  specialRules: string[];
}

interface SkillsCardProps {
  character: Character;
  onRoll: (skillId: SkillTestDef['id'], extraMod?: { label: string; value: number }) => void;
  /** Right-click: opposed vs the sheet's opponent. Absent = inert. */
  onOpposed?: ((skillId: SkillTestDef['id']) => boolean) | null;
  /** Weapons available for the parry selector (attacker: character.weapons; defender: source character's). */
  weapons?: ParryWeaponOption[];
  /**
   * Skills a CONDITION forbids, keyed by id, the value being the reason.
   * Distinct from the untrained tag: untrained is a note on a roll you may
   * still make, this is a roll you may not.
   */
  blocked?: Record<string, string>;
  /**
   * Condition penalty per skill id - the SAME value handed to `onRoll`.
   *
   * Without it the row showed the sheet's own number while the roll went
   * out at something else, which reads as the condition doing nothing.
   */
  conditionMods?: Record<string, number>;
}

/** Melee, not Unwieldy - mirrors the defence chain's eligibility (findParryWeaponIndex). */
function parryCapable(weapons: ParryWeaponOption[]): ParryWeaponOption[] {
  return weapons.filter((w) => w.type === 'melee' && !hasWS(w.specialRules, 'Unwieldy'));
}

type ExtraMod = { label: string; value: number };

/**
 * The parry weapon choice: the parry-capable weapons, which one is
 * picked (default the defence chain's auto-best, the first), its
 * modifier, and the labelled extra it adds to a Parry roll (none at 0).
 */
function useParryChoice(weapons: ParryWeaponOption[]) {
  const options = useMemo(() => parryCapable(weapons), [weapons]);
  const [index, setIndex] = useState(0);
  const selected = options[Math.min(index, Math.max(0, options.length - 1))];
  const mod = selected ? calcWeaponParryMod(selected.specialRules) : 0;
  const extra: ExtraMod | undefined =
    selected && mod !== 0
      ? { label: `${selected.name} (${selected.specialRules.join(', ') || 'weapon'})`, value: mod }
      : undefined;
  return { options, index, setIndex, mod, extra };
}

type ParryChoice = ReturnType<typeof useParryChoice>;

/** "+10", "-5" or "±0" - the parry weapon's modifier beside the selector. */
function parryModText(mod: number): string {
  if (mod === 0) return '±0';
  return mod > 0 ? `+${mod}` : `${mod}`;
}

function ParryWeaponRow({ parry }: { parry: ParryChoice }) {
  return (
    <div className={styles.parryWeaponRow}>
      <span className={styles.skillGoverning}>with:</span>
      <select
        className={styles.parryWeaponSelect}
        value={parry.index}
        onChange={(e) => parry.setIndex(parseInt(e.target.value, 10) || 0)}
        aria-label="Parry weapon"
      >
        {parry.options.map((w, i) => (
          <option key={i} value={i}>
            {w.name}
          </option>
        ))}
      </select>
      <span className={styles.skillGoverning}>{parryModText(parry.mod)}</span>
    </div>
  );
}

/** A condition modifier as a tag: "+10" or "−20" (a true minus sign). */
function signedTag(n: number): string {
  return `${n > 0 ? '+' : '\u2212'}${Math.abs(n)}`;
}

interface TagsProps {
  skillId: string;
  block: string | undefined;
  untrained: boolean;
  rollable: boolean;
  condMod: number;
}

/** Why the roll is refused, or the untrained and condition tags. */
function SkillTags({ skillId, block, untrained, rollable, condMod }: TagsProps) {
  if (block) {
    return (
      <span className={styles.untrainedTag} data-testid={`blocked-${skillId}`}>
        {block}
      </span>
    );
  }
  return (
    <>
      {untrained && rollable && <span className={styles.untrainedTag}>untrained</span>}
      {condMod !== 0 && rollable && (
        <span className={styles.untrainedTag} data-testid={`cond-mod-${skillId}`}>
          {signedTag(condMod)}
        </span>
      )}
    </>
  );
}

/** The button's hover text: the block reason, the roll it makes, or why it cannot. */
function skillTitle(label: string, block: string | undefined, rollable: boolean, target: number): string {
  if (block) return block;
  if (rollable) return `Roll a ${label} test (d100 vs ${target} + modifiers)`;
  return `${label} is an advanced skill - cannot be tested untrained`;
}

/** Right-click: an opposed test, when the sheet has an opponent; otherwise the browser's own menu. */
function opposeOnRightClick(onOpposed: SkillsCardProps['onOpposed'], skillId: SkillTestDef['id']) {
  return (e: React.MouseEvent) => {
    if (!onOpposed) return;
    e.preventDefault();
    onOpposed(skillId);
  };
}

interface RowProps extends Pick<SkillsCardProps, 'character' | 'onRoll' | 'onOpposed'> {
  def: SkillTestDef;
  block: string | undefined;
  condMod: number;
  parry: ParryChoice;
}

function SkillRow({ def, character, onRoll, onOpposed, block, condMod, parry }: RowProps) {
  const { base, untrained, rollable } = calcSkillTarget(character, def.id);
  const isParry = def.id === 'parry';
  const shownTarget = (isParry ? base + parry.mod : base) + condMod;
  return (
    <div className={styles.skillCardRow}>
      <button
        type="button"
        className={styles.skillBtn}
        disabled={!rollable || !!block}
        title={skillTitle(def.label, block, rollable, shownTarget)}
        aria-label={`Roll ${def.label} test`}
        onClick={() => onRoll(def.id, isParry ? parry.extra : undefined)}
        onContextMenu={opposeOnRightClick(onOpposed, def.id)}
      >
        <span className={styles.charLabel}>
          {def.label} <span className={styles.skillGoverning}>({CHARACTERISTIC_TEST_LABELS[def.governing]})</span>
        </span>
        <span>
          <SkillTags skillId={def.id} block={block} untrained={untrained} rollable={rollable} condMod={condMod} />{' '}
          <span className={styles.charValue}>{rollable ? shownTarget : '-'}</span>
        </span>
      </button>
      {isParry && parry.options.length > 0 && <ParryWeaponRow parry={parry} />}
    </div>
  );
}

function SkillsCard({
  character,
  onRoll,
  onOpposed = null,
  weapons = [],
  blocked = {},
  conditionMods = {},
}: SkillsCardProps) {
  const parry = useParryChoice(weapons);
  return (
    <div className={styles.skillsCardList} data-testid="skill-test-list">
      {SKILL_TEST_DEFS.map((def) => (
        <SkillRow
          key={def.id}
          {...{ def, character, onRoll, onOpposed, parry }}
          block={blocked[def.id]}
          condMod={conditionMods[def.id] ?? 0}
        />
      ))}
    </div>
  );
}

export default memo(SkillsCard);
